import { Link } from '@tanstack/react-router'
import { scoreColor } from '@workspace/seedforge-shared'
import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@workspace/ui/components/tooltip'
import { cn } from '@workspace/ui/lib/utils'
import { HelpCircle } from 'lucide-react'
import type { EvalJudgeCard } from '../types'
import { ConfidenceBar } from './confidence-bar'

const PERSONA_DESCRIPTIONS: Record<string, { title: string; description: string }> = {
  oracle: {
    title: 'Vision & Strategy Analyst',
    description:
      'Analytical and forward-thinking. Evaluates long-term vision, market positioning, and strategic clarity.',
  },
  hustler: {
    title: 'Traction & Execution Analyst',
    description:
      "Direct and metrics-driven. Focuses on execution speed, market traction, and the founder's ability to ship and sell.",
  },
  architect: {
    title: 'Product & Technical Analyst',
    description:
      'Detail-oriented and methodical. Assesses product architecture, technical feasibility, and engineering capability.',
  },
  hawk: {
    title: 'Risk & Due Diligence Analyst',
    description:
      'Skeptical and thorough. Scrutinizes risks, financials, market threats, and potential failure modes.',
  },
}

interface JudgeCardProps {
  judge: EvalJudgeCard
  detailPath: string
}

export { JudgeCard as EvaluationJudgeCard }

export function JudgeCard({ judge, detailPath }: JudgeCardProps) {
  const meta = PERSONA_DESCRIPTIONS[judge.key]

  return (
    <Link
      to={detailPath}
      className="rounded-xl border border-border bg-card/30 p-5 transition-all hover:border-[#f97316]/30 hover:bg-card/50"
    >
      {/* Header: avatar + name + verdict + score */}
      <div className="flex items-center gap-3">
        <Avatar className="size-10">
          <AvatarFallback
            className="text-xs font-bold text-white"
            style={{ backgroundColor: `${judge.color}20` }}
          >
            <span style={{ color: judge.color }}>{judge.initials}</span>
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-foreground">{judge.persona}</p>
            {meta && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="text-muted-foreground/60 transition-colors hover:text-foreground"
                    onClick={(e) => e.preventDefault()}
                  >
                    <HelpCircle className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={4} className="max-w-64">
                  <p className="font-medium">{meta.title}</p>
                  <p className="mt-0.5 opacity-70">{meta.description}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p
            className={cn(
              'text-[10px] font-medium',
              judge.verdictType === 'positive' ? 'text-emerald-400' : 'text-muted-foreground'
            )}
          >
            {judge.verdict}
          </p>
        </div>
        <div className="text-right">
          <p className={cn('text-lg font-bold tabular-nums', scoreColor(judge.avgScore))}>
            {judge.avgScore}
          </p>
          <p className="text-[9px] text-muted-foreground/80">avg score</p>
        </div>
      </div>

      {/* Confidence bar */}
      <ConfidenceBar value={judge.confidence} color={judge.color} className="mt-3" />

      {/* Takeaway */}
      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{judge.takeaway}</p>

      {/* Strength + Concern */}
      <div className="mt-3 space-y-2">
        <div className="flex items-start gap-2">
          <span className="mt-1 size-1.5 shrink-0 rounded-full bg-emerald-400" />
          <span className="text-[11px] leading-relaxed text-muted-foreground">
            {judge.topStrength}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red-400" />
          <span className="text-[11px] leading-relaxed text-muted-foreground">
            {judge.topConcern}
          </span>
        </div>
      </div>

      {/* Quote */}
      <p className="mt-3 border-t border-border/50 pt-3 text-[11px] leading-relaxed text-muted-foreground/80 italic">
        {judge.quote}
      </p>
    </Link>
  )
}
